import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { roleService } from '../../services/roleService';
import { RoleDetail } from '../../types';
import '../Products/ProductCategoryDeletePage.css';
import { parseApiError } from '../../utils/errorUtils';

const RoleDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passedName = (location.state as { roleName?: string })?.roleName;

  useEffect(() => {
    if (!id) return;
    roleService
      .getRoleById(id)
      .then((res) => setRole(res.data?.data ?? null))
      .catch(() => setError(t('role.failedToLoad')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setError(null);
    try {
      await roleService.deleteRole(id);
      navigate('/roles', {
        state: { message: t('role.roleDeletedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('role.failedToDelete')).message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!role && !passedName) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('role.roleNotFound')}</div>
          <button className="delete-breadcrumb-btn" onClick={() => navigate('/roles')}>
            {t('role.backToRoles')}
          </button>
        </div>
      </Layout>
    );
  }

  const roleName = role?.name ?? passedName ?? '';
  const isSystem = role?.isSystem ?? false;
  const userCount = role?.userCount ?? 0;
  const canDelete = !isSystem && userCount === 0;

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button className="delete-breadcrumb-btn" onClick={() => navigate('/roles')}>
            <ArrowLeft size={16} strokeWidth={2} />
            {t('role.backToRoles')}
          </button>
        </nav>

        <div className="delete-content">
          {/* Main card */}
          <div className="delete-card delete-card-main">
            <div className="delete-card-header">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('role.deleteTitle')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show" style={{ margin: '16px' }}>
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            )}

            <div className="delete-card-body">
              {/* System role block */}
              {isSystem && (
                <div className="delete-warning-section">
                  <p className="delete-warning-text">{t('role.cannotDeleteSystem')}</p>
                </div>
              )}

              {/* Has users block */}
              {!isSystem && userCount > 0 && (
                <div className="delete-warning-section">
                  <p className="delete-warning-text">
                    {t('role.cannotDeleteWithUsers').replace('{count}', String(userCount))}
                  </p>
                </div>
              )}

              {/* Safe to delete block */}
              {canDelete && (
                <div className="delete-warning-section">
                  <p className="delete-warning-text">{t('role.deleteConfirmMessage')}</p>
                  <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
                    {t('role.deleteWarning')}
                  </p>
                </div>
              )}

              {/* Role info */}
              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('role.roleName')}</label>
                  <p className="delete-info-value delete-code">{roleName}</p>
                </div>
                {role?.description && (
                  <div className="delete-info-item">
                    <label className="delete-info-label">{t('role.description')}</label>
                    <p className="delete-info-value">{role.description}</p>
                  </div>
                )}
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('role.type')}</label>
                  <p className="delete-info-value">
                    <span className={`badge ${isSystem ? 'badge-draft' : 'badge-active'}`}>
                      {isSystem ? t('role.systemRole') : t('role.customRole')}
                    </span>
                  </p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('role.permissionsCount')}</label>
                  <p className="delete-info-value">{role?.permissions?.length ?? 0}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('role.usersCount')}</label>
                  <p className="delete-info-value">{userCount}</p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate('/roles')}
                  disabled={deleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={!canDelete || deleting}
                >
                  {deleting ? (
                    <>
                      <span className="btn-spinner" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('role.confirmDelete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('role.safetyInfo')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('user.permanentAction')}</strong>
                    <p>{t('role.safetyNote')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('user.relatedData')}</strong>
                    <p>{t('role.dataNote')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RoleDeletePage;
